/**
 * Action bar: download the chart as a PNG, copy a link to the current
 * trajectory, or clear everything.
 *
 * Purely presentational — every action is a callback owned by `App`, which also
 * owns the clipboard fallback and the export itself. There is no file import or
 * export any more: sharing travels in the URL fragment.
 *
 * Every action that can fail or take a moment reports it inside its own button:
 * the label is a polite live region, so the text changing is the whole
 * announcement and the app needs no banner, toast or separate status line. The
 * label also carries a fixed minimum width, so a longer state can neither resize
 * its button nor reflow the row on a phone. Clear all has no such state on
 * purpose: the list emptying is its feedback.
 */

import type { ReactElement } from 'react'

/** What the Copy link button is showing. */
export type CopyStatus = 'idle' | 'copied' | 'failed'

/** What the Download PNG button is showing. */
export type DownloadStatus = 'idle' | 'preparing' | 'failed'

const COPY_LABELS: Record<CopyStatus, string> = {
  idle: 'Copy link',
  copied: 'Copied',
  failed: "Couldn't copy",
}

const DOWNLOAD_LABELS: Record<DownloadStatus, string> = {
  idle: 'Download PNG',
  preparing: 'Preparing…',
  failed: 'Export failed',
}

/**
 * The success checkmark that rides with "Copied". Decorative — the word beside
 * it carries the meaning — and hand-written rather than an icon dependency,
 * because it is one path. `currentColor` lets the label's tone colour it.
 */
function Checkmark(): ReactElement {
  return (
    <svg
      className="toolbar__check"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M2.75 8.5 6.25 12l7-8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * A button's label, and the live region that reports a change in it. The
 * announcement is a change *inside* the control the user just pressed, so it
 * needs no separate region, banner or toast — and because the label is mounted
 * from the first render, the change is announced rather than the region's
 * arrival. The span's width is fixed in CSS, so a longer state cannot resize the
 * button it sits in.
 */
function ButtonLabel({ text, copied = false }: { text: string; copied?: boolean }): ReactElement {
  return (
    <span
      className={copied ? 'toolbar__label toolbar__label--copied' : 'toolbar__label'}
      aria-live="polite"
    >
      {copied ? <Checkmark /> : null}
      {text}
    </span>
  )
}

export interface ToolbarProps {
  /** PNG export needs at least one entry to be meaningful. */
  canDownloadPng: boolean
  /**
   * A link with no payload would decode to `[]` on the recipient's side, so
   * copying one is disabled rather than quietly sharing nothing.
   */
  canCopyLink: boolean
  /** `preparing` disables the button; `failed` is shown briefly, then reverts. */
  downloadStatus: DownloadStatus
  copyStatus: CopyStatus
  onDownloadPng: () => void
  onCopyLink: () => void
  onClearAll: () => void
}

export default function Toolbar({
  canDownloadPng,
  canCopyLink,
  downloadStatus,
  copyStatus,
  onDownloadPng,
  onCopyLink,
  onClearAll,
}: ToolbarProps): ReactElement {
  return (
    // No heading of its own: it sits under the chart it acts on, inside the
    // trajectory card, so it is labelled as a group instead of as a section.
    <div className="toolbar" role="group" aria-label="Chart actions">
      <div className="toolbar__row">
        <button
          type="button"
          className={
            downloadStatus === 'failed'
              ? 'button button--primary button--error'
              : 'button button--primary'
          }
          onClick={onDownloadPng}
          disabled={!canDownloadPng || downloadStatus === 'preparing'}
        >
          <ButtonLabel text={DOWNLOAD_LABELS[downloadStatus]} />
        </button>
        <button
          type="button"
          className={
            copyStatus === 'failed'
              ? 'button button--secondary button--error'
              : 'button button--secondary'
          }
          disabled={!canCopyLink}
          onClick={onCopyLink}
        >
          <ButtonLabel text={COPY_LABELS[copyStatus]} copied={copyStatus === 'copied'} />
        </button>
        <button type="button" className="button button--danger" onClick={onClearAll}>
          <ButtonLabel text="Clear all" />
        </button>
      </div>
    </div>
  )
}
